// eslint-disable-next-line medely/noUseCallbackUseMemo
import { useMemo, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { get, isEmpty, omit, set } from 'lodash';
import { sessionTools } from '../utils/sessionTools';

export type UseSessionLocalStorageResult<T> = {
  clearValue: () => Promise<void>;
  setValue: (_: T) => Promise<void>;
  value: T;
  isLoading: boolean;
};

const useSessionLocalStorage = <T>(
  objectPath: string,
  initialValue: T,
): UseSessionLocalStorageResult<T> => {
  // eslint-disable-next-line medely/noUseCallbackUseMemo
  const { key, path } = useMemo(() => parseKey(objectPath), [objectPath]);
  const [isLoading, setIsLoading] = useState(true);
  const isNestedProperty = key !== objectPath;
  const sessionKey = `${key}-session`;

  const [value, setValue] = useState<T>(initialValue);

  const clearValue = async () => {
    setValue(initialValue);

    if (isNestedProperty) {
      const item = (getLocalStorageItem(key) ?? {}) as object;
      const result = omit(item, path) as object;
      if (isEmpty(result)) {
        await AsyncStorage.removeItem(key);
        return;
      }

      await AsyncStorage.setItem(key, JSON.stringify(result));
      return;
    }

    await AsyncStorage.removeItem(key);
  };

  useEffect(() => {
    let componentMounted = true;

    const validateSession = async () => {
      const lastUsedSessionId = await getLocalStorageItem(sessionKey);
      const id = await sessionTools.getOrUpdateSessionId();
      const isSessionExpired = !!lastUsedSessionId && id !== lastUsedSessionId;

      if (!componentMounted) {
        return;
      }

      const stored = await getLocalStorageItem(key);
      const storedValue = isNestedProperty ? get(stored, path) : stored;

      isSessionExpired ? clearValue() : setValue(storedValue);

      await AsyncStorage.setItem(sessionKey, id);
      await sessionTools.recordActivity();

      setIsLoading(false);
    };

    validateSession();
    return () => {
      componentMounted = false;
    };
  }, []);

  const updateValue = async (value) => {
    if (value === initialValue) {
      return;
    }

    setValue(value);

    if (isNestedProperty) {
      const item = (await (getLocalStorageItem(key) ?? {})) as object;
      set(item, path, value);
      await AsyncStorage.setItem(key, JSON.stringify(item));
      return;
    }

    await AsyncStorage.setItem(key, JSON.stringify(value));
    sessionTools.recordActivity();
  };

  return {
    clearValue,
    value,
    setValue: updateValue,
    isLoading,
  };
};

export default useSessionLocalStorage;

const getLocalStorageItem = async <T>(key: string) => {
  const item = await AsyncStorage.getItem(key);

  if (isEmpty(item)) {
    return;
  }

  const parsedItem = parseValue<T>(item);
  return parsedItem;
};

const parseKey = (objectPath: string) => {
  const [key, ...childPath] = objectPath.split('.');

  return { key, path: childPath?.join('.') };
};

const parseValue = <T>(item: any) => {
  try {
    return JSON.parse(item) as T;
  } catch (_) {
    return item as T;
  }
};
